import { forwardRef, type InputHTMLAttributes } from "react";

/**
 * Formats a phone number string to (XXX) XXX-XXXX format
 */
export function formatPhoneNumber(value: string): string {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, "");

  // Limit to 10 digits
  const limited = digits.slice(0, 10);

  // Format based on length
  if (limited.length === 0) {
    return "";
  } else if (limited.length <= 3) {
    return `(${limited}`;
  } else if (limited.length <= 6) {
    return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  } else {
    return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
  }
}

/**
 * Extracts raw digits from a formatted phone number
 */
export function unformatPhoneNumber(value: string): string {
  return value.replace(/\D/g, "").slice(0, 10);
}

/**
 * Validates if a phone number has 10 digits
 */
export function isValidPhoneNumber(value: string): boolean {
  const digits = value.replace(/\D/g, "");
  return digits.length === 10;
}

interface PhoneInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "type"> {
  value: string;
  onChange: (value: string) => void;
  /** If true, stores formatted value. If false (default), stores raw digits */
  storeFormatted?: boolean;
}

/**
 * Phone input component with automatic (XXX) XXX-XXXX formatting
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ value, onChange, storeFormatted = false, className, ...props }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const formatted = formatPhoneNumber(inputValue);

      if (storeFormatted) {
        onChange(formatted);
      } else {
        // Store raw digits
        onChange(unformatPhoneNumber(inputValue));
      }
    };

    // Display formatted value
    const displayValue = formatPhoneNumber(value);

    return (
      <input
        ref={ref}
        type="tel"
        inputMode="numeric"
        autoComplete="tel"
        value={displayValue}
        onChange={handleChange}
        placeholder="(555) 123-4567"
        maxLength={14} // (XXX) XXX-XXXX = 14 chars
        className={className}
        {...props}
      />
    );
  }
);

PhoneInput.displayName = "PhoneInput";

export default PhoneInput;
